/**
 * Phase 5-A — Strategy wizard state hook.
 *
 * Owns the 5-step wizard's draft form state, step cursor, validation,
 * and localStorage persistence (keyed per user_id so switching accounts
 * doesn't bleed drafts). On a successful generation the caller invokes
 * `clearDraft()` to drop the WIP from storage.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY_PREFIX = "smartpublish_strategy_wip_";

export const DEFAULT_FORM = Object.freeze({
    niche: "",
    niche_other: "", // free-text when niche==="Other (specify)"
    duration_days: 15,
    goals: [],
    audience: "",
    tone: [],
    frequency: {
        reels_per_day: 1,
        stories_per_day: 2,
        posts_per_day: 1,
    },
    formats: [],
    product_offer: "",
});

export const STEP_LABELS = [
    "Niche",
    "Duration & Goals",
    "Audience & Tone",
    "Frequency & Formats",
    "Offer",
];

export function useStrategyState(userId) {
    const storageKey = userId
        ? `${STORAGE_KEY_PREFIX}${userId}`
        : null;

    const [step, setStep] = useState(1);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [hydrated, setHydrated] = useState(false);

    // Hydrate from localStorage once per userId. Fall through gracefully if
    // the payload is malformed — we never want a corrupted draft to crash
    // the wizard.
    useEffect(() => {
        if (!storageKey) {
            setHydrated(true);
            return;
        }
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object") {
                    setForm({ ...DEFAULT_FORM, ...parsed.form });
                    if (typeof parsed.step === "number") {
                        setStep(Math.max(1, Math.min(5, parsed.step)));
                    }
                }
            }
        } catch {
            /* ignore malformed drafts */
        }
        setHydrated(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // Persist every form/step change (debounced via microtask).
    useEffect(() => {
        if (!storageKey || !hydrated) return;
        try {
            window.localStorage.setItem(
                storageKey,
                JSON.stringify({ form, step })
            );
        } catch {
            /* storage full / disabled — silently ignore */
        }
    }, [form, step, storageKey, hydrated]);

    const clearDraft = useCallback(() => {
        if (!storageKey) return;
        try {
            window.localStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
        setForm(DEFAULT_FORM);
        setStep(1);
    }, [storageKey]);

    const updateField = useCallback((key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const toggleArrayField = useCallback((key, value, max = 3) => {
        setForm((prev) => {
            const cur = prev[key] || [];
            if (cur.includes(value)) {
                return { ...prev, [key]: cur.filter((x) => x !== value) };
            }
            if (cur.length >= max) return prev;
            return { ...prev, [key]: [...cur, value] };
        });
    }, []);

    // Normalize niche → resolve "Other" into the free-text value.
    const resolvedNiche = useMemo(() => {
        if (form.niche === "Other (specify)") {
            return (form.niche_other || "").trim();
        }
        return form.niche.trim();
    }, [form.niche, form.niche_other]);

    const stepValid = useMemo(() => {
        return {
            1: resolvedNiche.length > 0,
            2:
                [7, 15, 30].includes(form.duration_days) &&
                form.goals.length >= 1 &&
                form.goals.length <= 3,
            3:
                form.audience.trim().length > 0 &&
                form.tone.length >= 1 &&
                form.tone.length <= 3,
            4:
                form.formats.length >= 1 &&
                form.frequency.reels_per_day >= 0 &&
                form.frequency.stories_per_day >= 0 &&
                form.frequency.posts_per_day >= 0,
            5: form.product_offer.trim().length >= 10,
        };
    }, [form, resolvedNiche]);

    const allValid = useMemo(
        () => [1, 2, 3, 4, 5].every((n) => stepValid[n]),
        [stepValid]
    );

    const nextStep = useCallback(() => {
        setStep((s) => {
            if (!stepValid[s]) return s;
            return Math.min(5, s + 1);
        });
    }, [stepValid]);

    const prevStep = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);

    // Server-ready payload (strip local-only `niche_other` field).
    const serverPayload = useMemo(
        () => ({
            niche: resolvedNiche,
            duration_days: form.duration_days,
            goals: form.goals,
            audience: form.audience.trim(),
            tone: form.tone,
            frequency: form.frequency,
            formats: form.formats,
            product_offer: form.product_offer.trim(),
        }),
        [form, resolvedNiche]
    );

    return {
        step,
        setStep,
        form,
        setForm,
        updateField,
        toggleArrayField,
        stepValid,
        allValid,
        nextStep,
        prevStep,
        resolvedNiche,
        serverPayload,
        clearDraft,
        hydrated,
    };
}
